import type { RunReadiness } from '@/shell/api/queries'
import { Zap } from '@untitledui/icons'
import { Focusable } from 'react-aria-components'
import { Tooltip } from '@/components/base/tooltip/tooltip'
import { cx } from '@/utils/cx'

/**
 * One assignee cell for every work-item list and the drawer (tasks review, Sean 2026-09-20 22:02): avatar or ⚡, the
 * name, and the agent's state as a quiet suffix. One fact, one indicator: the R0 "Waiting for agent" chip (QA-29)
 * said the runtime did not exist; it does now, so the suffix says what the agent is doing or what stops it from
 * doing anything. The name truncates; the suffix never does.
 *
 * Prior art: Linear's assignee cell (avatar + name, state elsewhere), GitHub's "assigned to" with a status word.
 */
export interface AssigneeLike {
  readonly assignee_name?: string | null
  readonly assignee_kind?: 'person' | 'agent' | string | null
  readonly assigneeKind?: 'person' | 'agent' | string | null
  readonly status: string
  readonly run_readiness?: RunReadiness | null
  readonly runReadiness?: RunReadiness | null
}

/** The New work item assignee picker's words for the file agent: a hand-made item binds no skill, so nothing runs on it by itself. */
export const AGENT_HAND_OFF_LABEL = 'file agent · no skill for this item'
export const AGENT_HAND_OFF_HINT = 'A hand-made item has no skill bound, so the agent runs nothing on it. It sits on the agent until a person takes it over, or a template row binds a skill.'

/**
 * The quiet suffix after an agent's name: what it is doing, or what stops anything from running. Person rows have
 * no suffix (their status is the Status column). Statuses the Status pill already names (blocked, waiting on you,
 * review, done, deferred) get none either, so the row never says one thing twice.
 */
export function assigneeSuffix(item: AssigneeLike): string | null {
  const kind = item.assigneeKind ?? item.assignee_kind
  if (kind !== 'agent')
    return null
  const readiness = item.runReadiness ?? item.run_readiness ?? null
  switch (item.status) {
    case 'running': return 'running'
    case 'scheduled': return readiness === 'ready' ? 'scheduled' : whatStopsIt(readiness)
    case 'not_started':
    case 'open':
      return readiness === 'ready' ? 'ready to run' : whatStopsIt(readiness)
    default: return null
  }
}

const NEEDS_DEFINITIONS = 'needs Definitions'
const NO_SKILL = 'no skill for this item'
const NO_PERMISSIONS = 'no permissions yet'

function whatStopsIt(readiness: RunReadiness | null): string | null {
  switch (readiness) {
    case 'needs_definitions': return NEEDS_DEFINITIONS
    case 'no_skill': return NO_SKILL
    case 'no_permissions': return NO_PERMISSIONS
    case 'ready': return null
    default: return null
  }
}

export interface SuffixHelp {
  readonly title: string
  readonly description: string
}

/**
 * Plain words behind the two "what stops it" suffixes, shown on hover or focus (Sean, merge-train review 2026-09-21:
 * "needs Definitions" reads fine but may need a tooltip). Written for a partner CFO who has never heard Palomino
 * explain Definitions; the running / scheduled / ready suffixes need no gloss.
 */
const SUFFIX_HELP: Readonly<Record<string, SuffixHelp>> = {
  [NEEDS_DEFINITIONS]: {
    title: 'Needs Definitions',
    description: 'The skill for this item needs a few firm or client settings (Definitions) filled in before the file agent can run it. Open Definitions to complete them.',
  },
  [NO_SKILL]: {
    title: 'No skill for this item',
    description: AGENT_HAND_OFF_HINT,
  },
  // UX review 2026-09-21 note 9 (Seb's copy, ui-standards D27): "permissions", never allowlist or write types.
  [NO_PERMISSIONS]: {
    title: 'No permissions yet',
    description: 'Your agents haven\'t been assigned any permissions yet, so they can\'t take any close actions. A CFO sets them under the file\'s Agent section.',
  },
}

/** The tooltip for an item's suffix, or null when the suffix speaks for itself (or there is none). */
export function assigneeSuffixHelp(item: AssigneeLike): SuffixHelp | null {
  const suffix = assigneeSuffix(item)
  return suffix ? SUFFIX_HELP[suffix] ?? null : null
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1]![0] ?? '' : ''
  return `${first}${last}`.toUpperCase() || '?'
}

export function AssigneeCell({ item, className }: { item: AssigneeLike & { assignee_name?: string | null }, className?: string }) {
  const kind = item.assigneeKind ?? item.assignee_kind
  const name = item.assignee_name ?? ''
  const suffix = assigneeSuffix(item)
  const help = assigneeSuffixHelp(item)
  const agent = kind === 'agent'
  const suffixNode = suffix
    ? (
        <span className={cx('shrink-0 whitespace-nowrap text-quaternary', help && 'cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--shell-focus-ring)]')} data-testid="assignee-suffix" data-help={help ? 'true' : undefined} tabIndex={help ? 0 : undefined}>
          <span aria-hidden>{'· '}</span>
          <span className="sr-only">{`${agent ? 'Agent' : ''} state: `}</span>
          {suffix}
        </span>
      )
    : null
  return (
    <span className={cx('flex min-w-0 items-center gap-1.5 text-xs text-tertiary', className)} data-testid="assignee-cell" data-assignee-kind={agent ? 'agent' : 'person'} data-suffix={suffix ?? undefined}>
      {/* 20 px so the row keeps its height; the Untitled UI Avatar starts at 24 px. Agent = ⚡ on the brand tint, person = initials. */}
      <span className={cx('flex size-5 shrink-0 items-center justify-center rounded-full', agent ? 'bg-brand-secondary' : 'bg-secondary')} aria-hidden data-testid={agent ? 'assignee-agent-mark' : 'assignee-avatar'}>
        {agent ? <Zap className="size-3 text-fg-brand-secondary" /> : <span className="text-[9px] font-semibold leading-none text-quaternary">{initialsOf(name)}</span>}
      </span>
      <span className="min-w-0 truncate text-secondary" data-testid="assignee-name">{name}</span>
      {/* The two "what stops it" suffixes carry a tooltip in partner words (hover or focus); the others speak for themselves. */}
      {help && suffixNode
        ? (
            <Tooltip title={help.title} description={help.description} placement="bottom start">
              <Focusable>{suffixNode}</Focusable>
            </Tooltip>
          )
        : suffixNode}
    </span>
  )
}
